import { DensityUnits } from "@/libs/units/density";
import { FrequencyUnits } from "@/libs/units/frequency";
import { PercentageUnits } from "@/libs/units/percentage";
import type { RenderScenarioExportCols } from "./sizingExportTypes";

export const renderPumpIntakeCalcsCols: RenderScenarioExportCols = ({
  scenario,
  calcs,
  targetUnits
}) => {
  const sectionTitle = calcs.isScenarioEspShore.value
    ? "Pump/Intake Calculations"
    : "Pump/Suction Calculations";
  return [
    {
      section: sectionTitle,
      label: "Bottom of Intake Depth (MD)",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.equipmentLengths.bottomOfIntakesDepthFt,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Bottom of Intake Depth (TVD)",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.equipmentLengths.bottomOfIntakesTVDFt,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Frequency",
      targetUnit: FrequencyUnits.Hertz,
      value: calcs.adjustedPumpHzFrequency
    },
    {
      section: sectionTitle,
      label: "Tubing Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: scenario.parameters.value?.bottom_tubing_pressure_psia,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Casing Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: scenario.parameters.value?.bottom_casing_pressure_psia,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Well Flowing Pressure at Datum",
      targetUnit: targetUnits.pressureUnit,
      value: calcs.wellFlowingPressureAtDatumPSIA.value,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Well Flowing Pressure at Perfs",
      targetUnit: targetUnits.pressureUnit,
      value: calcs.wellFlowingPressureAtPerfsPSIA.value,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Total Flow Rate Entering Pump",
      overrideLabel: { newLabel: "bbl/d", labelToReplace: "STB/D" },
      targetUnit: targetUnits.liquidFlowUnit,
      value:
        calcs.pumpCalculations.individual.value[
          calcs?.ascendingOrderPumps?.value[0]?.id
        ]?.intakeStageCalculations?.intakeFluidPropertiesContext.fluidProperties
          .fluidFlowRateBBLD
    },
    {
      section: sectionTitle,
      label: calcs.isScenarioEspShore.value
        ? "Pump Intake Temperature"
        : "HPS Inlet Temperature",
      targetUnit: targetUnits.tempUnit,
      value: calcs.pumpIntakeTemperatureF.value
    },
    {
      section: sectionTitle,
      label: calcs.isScenarioEspShore.value
        ? "Pump Intake Pressure"
        : "Pump Inlet Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: calcs.pumpIntakePressurePSIA.value
    },
    {
      section: sectionTitle,
      label: "Free Gas Entering Intake",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        calcs.fluidProperties.fluidPropertiesAtIntake.value.gasFlowVolumeRatio
      )
    },
    {
      section: sectionTitle,
      label: "Turpin Coefficient",
      targetUnit: null,
      value: calcs.intakeCalculations.aggregate.turpinCoefficient.value
    },
    {
      section: sectionTitle,
      label: "Natural Separation Efficiency",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        calcs.intakeCalculations.aggregate.naturalGasSeparationEfficiencyRatio
          .value
      ),
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Gas Separator Efficiency",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        calcs.intakeCalculations.aggregate.intakeGasSeparationEfficiencyRatio
          .value
      ),
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Override Calculated Efficiency",
      targetUnit: PercentageUnits.Percentage,
      value: scenario.parameters.value?.intake_packer_installed
        ? 0
        : scenario.parameters.value?.intake_override_gas_separation_percent,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Packer Installed Above ESP",
      targetUnit: null,
      value: scenario.parameters.value?.intake_packer_installed ? "YES" : "NO",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Total Flow Rate Entering Intake",
      overrideLabel: { newLabel: "bbl/d", labelToReplace: "STB/D" },
      targetUnit: targetUnits.liquidFlowUnit,
      value:
        calcs.fluidProperties.fluidPropertiesAtIntake.value.fluidFlowRateBBLD
    },
    {
      section: sectionTitle,
      label: "Free Gas Entering Pump",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
          .gasFlowVolumeRatio
      )
    },
    {
      section: sectionTitle,
      label: "GOR after separation",
      targetUnit: targetUnits.liquidRatioUnit,
      value:
        calcs.fluidProperties.fluidPropertiesAfterAllGasSeparationAtPump.value
          .gasOilRatioSCFSTB
    },
    {
      section: sectionTitle,
      label: "Liquid Above Pump (TVD)",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.intakeCalculations.aggregate.liquidAbovePumpTvdFt,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Liquid Above Pump (MD)",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.intakeCalculations.aggregate.liquidAbovePumpMDFt,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Required TDH",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.pumpCalculations.aggregate.requiredTotalDynamicHeadFt
    },
    {
      section: sectionTitle,
      label: "Fluid Lift TDH required",
      targetUnit: targetUnits.largeLengthUnit,
      value: "",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Fluid Lift TDH required %",
      targetUnit: PercentageUnits.Percentage,
      value: "",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Friction Loss on Tubing TDH",
      targetUnit: targetUnits.largeLengthUnit,
      value: "",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Friction Loss on Tubing TDH %",
      targetUnit: PercentageUnits.Percentage,
      value: "",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Tubing Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: scenario.parameters.value?.bottom_tubing_pressure_psia,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Tubing Pressure %",
      targetUnit: PercentageUnits.Percentage,
      value: "",
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Casing Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: scenario.parameters.value?.bottom_casing_pressure_psia,
      hidden: !calcs.isScenarioEspShore.value
    },
    {
      section: sectionTitle,
      label: "Calc Head Gas Derate",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        calcs.pumpCalculations.aggregate.gasHeadRerateRatio.value
      )
    },
    {
      section: sectionTitle,
      label: "Pump Wear Head Correction",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        scenario.parameters.value?.pump_wear_head_correction_ratio
      )
    },
    {
      section: sectionTitle,
      label: "Pump Wear BHP Correction",
      targetUnit: PercentageUnits.Percentage,
      value: PercentageUnits.Ratio.toStandardUnit(
        scenario.parameters.value?.pump_wear_brake_horsepower_correction_ratio
      )
    },
    {
      section: sectionTitle,
      label: "Override Gas Head Rerate",
      targetUnit: PercentageUnits.Percentage,
      value: scenario.parameters.value?.pump_override_gas_correction_percent
    },
    {
      section: sectionTitle,
      label: "Required Discharge Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: calcs.pumpCalculations.aggregate.requiredDischargePressurePSIA
    },
    {
      section: sectionTitle,
      label: "Produced TDH",
      targetUnit: targetUnits.largeLengthUnit,
      value: calcs.pumpCalculations.aggregate.producedTotalDynamicHeadFt
    },
    {
      section: sectionTitle,
      label: "Produced Discharge Pressure",
      targetUnit: targetUnits.pressureUnit,
      value: calcs.pumpCalculations.aggregate.producedDischargePressurePSIA
    },
    {
      section: sectionTitle,
      label: "Fluid Specific Gravity at Intake",
      targetUnit: DensityUnits.SpecificGravity,
      decimalPlaceOverride: 4,
      value:
        calcs.fluidProperties.fluidPropertiesAtIntake.value.fluidSpecificGravity
    },
    {
      section: sectionTitle,
      label: "Fluid Specific Gravity at Pump Intake",
      targetUnit: DensityUnits.SpecificGravity,
      decimalPlaceOverride: 4,
      value:
        calcs.fluidProperties.fluidPropertiesAtPumpIntake.value
          .fluidSpecificGravity
    },
    {
      section: sectionTitle,
      label: "Fluid Specific Gravity at Pump Discharge",
      targetUnit: DensityUnits.SpecificGravity,
      decimalPlaceOverride: 4,
      value:
        calcs.pumpCalculations.individual.value[
          calcs.descendingOrderPumps.value[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidSpecificGravity
    },
    {
      section: sectionTitle,
      label: "Average Pump Fluid Specific Gravity",
      targetUnit: DensityUnits.SpecificGravity,
      decimalPlaceOverride: 4,
      value: calcs.pumpCalculations.aggregate.averageSpecificGravity
    },
    {
      section: sectionTitle,
      label: "Intake Fluid Density",
      targetUnit: targetUnits.densityUnit,
      value: DensityUnits.PoundsPerCubicFoot.toStandardUnit(
        calcs.pumpCalculations.individual.value[
          calcs.ascendingOrderPumps.value[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidDensityLBMFT3 || 0
      )
    },
    {
      section: sectionTitle,
      label: "Discharge Fluid Density",
      targetUnit: targetUnits.densityUnit,
      value: DensityUnits.PoundsPerCubicFoot.toStandardUnit(
        calcs.pumpCalculations.individual.value[
          calcs.descendingOrderPumps.value[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidDensityLBMFT3 || 0
      )
    },
    {
      section: sectionTitle,
      label: "Discharge Fluid Denstiy",
      targetUnit: targetUnits.densityUnit,
      value: DensityUnits.PoundsPerCubicFoot.toStandardUnit(
        calcs.pumpCalculations.aggregate.averageFluidDensityLBMFT3.value
      )
    },
    {
      section: sectionTitle,
      label: "Intake Fluid Viscosity",
      targetUnit: targetUnits.viscosityUnit,
      value:
        calcs.pumpCalculations.individual.value[
          calcs.ascendingOrderPumps.value[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidViscosityCP || 0
    },
    {
      section: sectionTitle,
      label: "Discharge Fluid Viscosity",
      targetUnit: targetUnits.viscosityUnit,
      value:
        calcs.pumpCalculations.individual.value[
          calcs.descendingOrderPumps.value[0]?.id
        ]?.dischargeStageCalculations?.dischargeFluidPropertiesContext
          .fluidProperties.fluidViscosityCP
    },
    {
      section: sectionTitle,
      label: "Average Fluid Viscosity",
      targetUnit: targetUnits.viscosityUnit,
      value: calcs.averageViscosityCP.value
    },
    {
      section: sectionTitle,
      label: "Intake Fluid Temperature",
      targetUnit: targetUnits.tempUnit,
      value: calcs.pumpIntakeTemperatureF.value
    },
    {
      section: sectionTitle,
      label: "Discharge Fluid Temperature",
      targetUnit: targetUnits.tempUnit,
      value: calcs.pumpCalculations.aggregate.dischargeTemperatureF.value
    },
    {
      section: sectionTitle,
      label: "Average Fluid Temperature",
      targetUnit: targetUnits.tempUnit,
      value: calcs.pumpAverageTemperatureF.value
    }
  ];
};
